/**
 * StubEntryStep — generic step-2 view for intents whose entry flow isn't
 * built yet (Spend / Travel / Shipment / Survey).
 *
 * Mirrors `ActivityEntryStep`'s layout — header + toolbar — so users get
 * a consistent shape across intents. Toolbar buttons come from
 * `INTENT_METHODS[intent]`; non-functional ones fire the existing Request
 * Access toast on click. If the intent has a provider section, a
 * "Connect" button opens the shared `ConnectProviderModal`.
 *
 * Survey is structurally different: instead of method buttons, the body
 * renders the survey-template tiles directly (each toasts Request Access
 * on click).
 */

import { useState, type ElementType } from 'react';
import { Plus, Upload, Camera, RefreshCw, Pencil, Send } from 'lucide-react';
import { Button, ButtonVariant } from '@g17eco/ui-modern/components/ui/button';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { generateToast } from '@g17eco/molecules/toasts';
import {
  INTENT_CARDS,
  INTENT_METHODS,
  type DataIntent,
  type DataMethod,
  type IntentWithMethods,
  type MethodArchetype,
  type MethodTileDescriptor,
  type MethodTileExternalRoute,
  type SurveyTile,
} from '../types';
import {
  INTENT_PROVIDER_SECTIONS,
  SURVEY_TILES,
} from '../requestAccessConfig';
import { fireProviderRequestAccessToast } from '../providerRequestAccess';
import { ConnectProviderModal } from './ConnectProviderModal';

const ARCHETYPE_ICON: Record<MethodArchetype, ElementType> = {
  manual: Plus,
  bulk: Upload,
  scan: Camera,
  connect: RefreshCw,
  existing: Pencil,
};

const isIntentWithMethods = (intent: DataIntent): intent is IntentWithMethods => intent !== 'survey';

const SurveyTileButton = ({ tile }: { tile: SurveyTile }) => {
  const { label, subject, Icon, iconBg, iconColor, titleColor } = tile;
  return (
    <button
      type='button'
      onClick={() =>
        generateToast({
          color: 'success',
          message: 'This feature is currently not enabled on your account. Please contact support to enable it.',
        })
      }
      className={cn(
        'tw:group tw:relative tw:flex tw:flex-col tw:items-center tw:gap-2 tw:rounded-xl tw:border tw:bg-white tw:text-center tw:transition-all tw:cursor-pointer',
        'tw:border-[#e5e7eb] tw:hover:border-emerald-400 tw:hover:-translate-y-[1px] tw:p-4',
      )}
    >
      <div
        className={cn('tw:flex tw:items-center tw:justify-center tw:rounded-2xl', iconBg)}
        style={{ width: 64, height: 64 }}
      >
        <Icon className={cn('tw:size-7', iconColor)} strokeWidth={1.7} />
      </div>
      <div className={cn('tw:text-sm tw:font-bold tw:leading-snug', titleColor)}>{label}</div>
      <div className='tw:text-[11px] tw:text-muted-foreground tw:leading-[1.35]'>{subject}</div>
      <div className='tw:absolute tw:top-2 tw:right-2 tw:flex tw:items-center tw:gap-1 tw:rounded-full tw:bg-emerald-600 tw:text-white tw:px-2 tw:py-0.5 tw:text-[10.5px] tw:font-semibold tw:opacity-0 tw:group-hover:opacity-100 tw:transition-opacity'>
        <Send className='tw:size-3' />
        Request Access
      </div>
    </button>
  );
};

const ToolbarMethodButton = ({
  tile,
  variant = 'outline',
  onSelectMethod,
  onNavigateExternal,
}: {
  tile: MethodTileDescriptor;
  variant?: ButtonVariant;
  onSelectMethod: (method: DataMethod) => void;
  onNavigateExternal: (route: MethodTileExternalRoute) => void;
}) => {
  const Icon = ARCHETYPE_ICON[tile.archetype];
  const handleClick = () => {
    if (tile.externalRoute) {
      onNavigateExternal(tile.externalRoute);
      return;
    }
    if (tile.requestAccessOnly) {
      fireProviderRequestAccessToast({ providerLabel: tile.subject, method: tile.method });
      return;
    }
    onSelectMethod(tile.method);
  };
  const label = tile.title ?? tile.subject;
  return (
    <Button size='sm' variant={variant} onClick={handleClick}>
      <Icon className='tw:size-4' />
      {label}
    </Button>
  );
};

export interface StubEntryStepProps {
  intent: DataIntent;
  /** Called when the user picks a real (non-stub) method. The parent owns routing. */
  onSelectMethod: (method: DataMethod) => void;
  /** Called when a method's `externalRoute` fires (e.g. "Manage existing" → manage-assets). */
  onNavigateExternal: (route: MethodTileExternalRoute) => void;
}

export const StubEntryStep = ({
  intent,
  onSelectMethod,
  onNavigateExternal,
}: StubEntryStepProps) => {
  const card = INTENT_CARDS.find((c) => c.id === intent);
  const methods = isIntentWithMethods(intent) ? INTENT_METHODS[intent] : [];
  const providerSection = INTENT_PROVIDER_SECTIONS[intent];
  const [connectModalOpen, setConnectModalOpen] = useState(false);

  return (
    <>
      <Card className='tw:mb-6'>
        <CardContent className='tw:p-6'>
          <div className='tw:flex tw:items-start tw:justify-between tw:mb-4 tw:gap-4'>
            <div>
              <h5 className='tw:font-semibold tw:mb-1'>{card?.title ?? 'Add data'}</h5>
              <div className='tw:text-sm tw:text-muted-foreground'>{card?.caption}</div>
            </div>
            <div className='tw:flex tw:gap-2 tw:items-center tw:flex-shrink-0'>
              {methods.map((tile, idx) => (
                <ToolbarMethodButton
                  key={`${tile.method}:${idx}`}
                  tile={tile}
                  variant={tile.variant}
                  onSelectMethod={onSelectMethod}
                  onNavigateExternal={onNavigateExternal}
                />
              ))}
              {providerSection && (
                <Button variant='outline' onClick={() => setConnectModalOpen(true)}>
                  <RefreshCw className='tw:size-4' />
                  Connect
                </Button>
              )}
            </div>
          </div>

          {intent === 'survey' ? (
            <div className='tw:rounded-xl tw:border tw:border-dashed tw:border-border tw:bg-muted/20 tw:p-5'>
              <div className='tw:text-sm tw:font-semibold tw:mb-3'>Choose your survey</div>
              <div className='tw:grid tw:grid-cols-2 tw:md:grid-cols-4 tw:gap-2.5'>
                {SURVEY_TILES.map((tile) => (
                  <SurveyTileButton key={tile.id} tile={tile} />
                ))}
              </div>
            </div>
          ) : (
            <div className='tw:rounded-xl tw:border tw:border-dashed tw:border-border tw:bg-muted/20 tw:p-12 tw:text-center'>
              <p className='tw:text-sm tw:text-muted-foreground tw:mb-1'>
                This entry flow isn't enabled yet on your account.
              </p>
              <p className='tw:text-xs tw:text-muted-foreground tw:mb-0'>
                Use the buttons above to request access — we'll notify you when it goes live.
              </p>
            </div>
          )}

        </CardContent>
      </Card>

      {providerSection && (
        <ConnectProviderModal
          isOpen={connectModalOpen}
          section={providerSection}
          onClose={() => setConnectModalOpen(false)}
        />
      )}
    </>
  );
};
